import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { getMemberInstitutes } from '@/utils/instituteRoles'

interface CurrentInstituteContextValue {
  currentInstituteId: string | null
  setCurrentInstituteId: (id: string | null) => void
  selectableInstitutes: string[]
  canSelectGlobal: boolean
}

const CurrentInstituteContext = createContext<CurrentInstituteContextValue>(
  {} as CurrentInstituteContextValue,
)

/**
 * Provider that manages the currently selected institute scope for the
 * authenticated user. Derives selectable institute IDs and the global-access
 * flag from claims, then keeps `currentInstituteId` in sync — defaulting
 * superusers to `'global'` and regular users to their first available institute.
 *
 * Must be mounted inside `AuthProvider`.
 */
export function CurrentInstituteProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const instituteRoles = user?.instituteRoles ?? {}
  const selectableInstitutes = useMemo(
    () => getMemberInstitutes(instituteRoles),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user],
  )
  const canSelectGlobal =
    user?.role === 'superuser' || instituteRoles.global === 'superuser'

  const [currentInstituteId, setCurrentInstituteId] = useState<string | null>(null)

  useEffect(() => {
    if (canSelectGlobal) {
      setCurrentInstituteId((previous) => {
        if (previous === 'global') return previous
        if (previous && selectableInstitutes.includes(previous)) return previous
        return 'global'
      })
      return
    }

    setCurrentInstituteId((previous) => {
      if (previous && selectableInstitutes.includes(previous)) return previous
      return selectableInstitutes[0] ?? null
    })
  }, [canSelectGlobal, selectableInstitutes])

  return (
    <CurrentInstituteContext.Provider
      value={{ currentInstituteId, setCurrentInstituteId, selectableInstitutes, canSelectGlobal }}
    >
      {children}
    </CurrentInstituteContext.Provider>
  )
}

/** Returns the current institute selection state. Must be used inside `CurrentInstituteProvider`. */
export function useCurrentInstituteContext() {
  return useContext(CurrentInstituteContext)
}
